/**
 * GlobalLoadingContext — estado de carga global para operaciones de larga duración.
 *
 * Activar: setLoading(true, 'Mensaje…')
 * Desactivar: setLoading(false)
 *
 * Consumidores actuales:
 *   - Header.tsx: restauración de backup
 *   - XMLProcessor.tsx: sincronización con IndexedDB
 *
 * El spinner visual se renderiza en GlobalSpinner.tsx,
 * montado en Layout.tsx (visible en todas las rutas).
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

interface GlobalLoadingState {
  loading: boolean;
  message: string;
  /** @param active - true para mostrar el spinner, false para ocultarlo. */
  setLoading: (active: boolean, msg?: string) => void;
}

const GlobalLoadingContext = createContext<GlobalLoadingState>({
  loading:    false,
  message:    '',
  setLoading: () => {},
});

export function GlobalLoadingProvider({ children }: { children: ReactNode }) {
  const [loading, setLoadingRaw] = useState(false);
  const [message, setMessage]    = useState('');

  const setLoading = useCallback((active: boolean, msg = '') => {
    setLoadingRaw(active);
    if (active) setMessage(msg);
  }, []);

  return (
    <GlobalLoadingContext.Provider value={{ loading, message, setLoading }}>
      {children}
    </GlobalLoadingContext.Provider>
  );
}

export function useGlobalLoading() {
  return useContext(GlobalLoadingContext);
}
